import React, { useEffect, useMemo, useState } from 'react';
import type { SIFTVehicle } from '../../types/sift';
import type { AgentHandoff } from '../agent/AgentChatWidget';
import { RdsMiaSummary } from './RdsMiaSummary';
import { LightBadge } from '../shared/LightBadge';
import { VMA_FIELD_HELP, VMA_PANEL_NOTE } from '../shared/vmaFieldHelp';
import { VinBuyersCard } from '../shared/VinBuyersCard';
import { VinCohortInsightsCard } from '../shared/VinCohortInsightsCard';
import { DaysBars } from '../shared/DaysBars';
import { LevelBadge, VelocityCaption } from '../shared/VmaLevels';
import {
  WS_SUPPLY_TONE, WS_DEMAND_TONE, RETAIL_SUPPLY_TONE, RETAIL_DEMAND_TONE,
} from '../shared/vmaLevelTones';
import { useVinCohortInsights } from '../shared/useVinCohortInsights';
import './VinDetailsPopout.css';
import { pushUsageContext, track } from '../../telemetry/usage';

interface VinDetailsPopoutProps {
  vehicle: SIFTVehicle | null;
  isLoading: boolean;
  onClose: () => void;
  onBack?: () => void;        // optional "back to dealer" affordance
  backLabel?: string;
  /** Mia's "Where to focus" for the unit (RDS-3.5); hidden when Mia is off for the user. */
  miaEnabled?: boolean;
  onContinueInMia?: (handoff: AgentHandoff) => void;
  /** Tablets and phones: the panel covers the screen and always offers a way back. */
  fullScreen?: boolean;
}

// ---------- formatters ----------
const fmtMoney = (n?: number | null) =>
  n == null ? '–' : `$${Math.round(n).toLocaleString()}`;
const fmtInt = (n?: number | null) =>
  n == null ? '–' : Math.round(n).toLocaleString();
const fmtStr = (v?: string | number | null) =>
  v == null || v === '' ? '–' : String(v);

const fmtDate = (raw?: string | null) => {
  if (!raw || !raw.trim()) return '–';
  const s = raw.trim();
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const cleaned = s.replace(' UTC', '');
    const d = new Date(cleaned + (cleaned.endsWith('Z') ? '' : 'Z'));
    if (isNaN(d.getTime())) return s;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return s;
  }
};

// ---------- small helpers ----------
const Kv: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="vin-kv">
    <span className={`vin-kv-label${hint ? ' vin-kv-label-help' : ''}`} title={hint}>
      {label}
    </span>
    <span className="vin-kv-value">{value}</span>
  </div>
);

const SummaryChip: React.FC<{ label: string; value: React.ReactNode; tooltip?: string }> = ({
  label,
  value,
  tooltip,
}) => (
  <div className="vin-chip" title={tooltip}>
    <span className="vin-chip-label">{label}</span>
    <span className="vin-chip-value">{value}</span>
  </div>
);

const Card: React.FC<{ title: string; className?: string; children: React.ReactNode }> = ({
  title,
  className,
  children,
}) => (
  <section className={`vin-card${className ? ' ' + className : ''}`}>
    <h3 className="vin-card-title">{title}</h3>
    {children}
  </section>
);

const Expander: React.FC<{ label: string; defaultOpen?: boolean; children: React.ReactNode }> = ({
  label,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="vin-exp">
      <button
        type="button"
        className="vin-exp-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} {label}
      </button>
      {open && <div className="vin-exp-body">{children}</div>}
    </div>
  );
};

// ---------- sortable table primitive ----------
type SortDir = 'asc' | 'desc';
interface SortEntry { key: string; dir: SortDir }

interface TableColumn<T> {
  key: string;
  label: string;
  sortValue: (row: T) => string | number | null | undefined;
  render: (row: T) => React.ReactNode;
  numeric?: boolean;
}

function useSort(initial: SortEntry[] = []) {
  const [sortBy, setSortBy] = useState<SortEntry[]>(initial);
  const handleSortClick = (key: string, e: React.MouseEvent) => {
    const additive = e.shiftKey;
    setSortBy((prev) => {
      const idx = prev.findIndex((s) => s.key === key);
      if (additive) {
        if (idx === -1) return [...prev, { key, dir: 'asc' }];
        const existing = prev[idx];
        if (existing.dir === 'asc') {
          const next = [...prev];
          next[idx] = { key, dir: 'desc' };
          return next;
        }
        return prev.filter((_, i) => i !== idx);
      }
      if (idx !== -1 && prev.length === 1) {
        const e2 = prev[idx];
        if (e2.dir === 'asc') return [{ key, dir: 'desc' }];
        return [];
      }
      return [{ key, dir: 'asc' }];
    });
  };
  return { sortBy, handleSortClick };
}

function compareValues(a: any, b: any, dir: SortDir): number {
  const aNull = a == null || (typeof a === 'number' && Number.isNaN(a));
  const bNull = b == null || (typeof b === 'number' && Number.isNaN(b));
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  let cmp = 0;
  if (typeof a === 'number' && typeof b === 'number') cmp = a - b;
  else cmp = String(a).localeCompare(String(b));
  return dir === 'asc' ? cmp : -cmp;
}

interface SortableTableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  searchPlaceholder?: string;
  searchableKeys?: (keyof T | string)[];
  rowKey: (row: T, idx: number) => string;
  emptyText?: string;
}

function SortableTable<T extends Record<string, any>>({
  columns,
  rows,
  searchPlaceholder,
  rowKey,
  emptyText = 'No data',
}: SortableTableProps<T>) {
  const [query, setQuery] = useState('');
  const { sortBy, handleSortClick } = useSort();

  const filtered = useMemo(() => {
    let arr = rows;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter((r) =>
        columns.some((c) => {
          const v = c.sortValue(r);
          return v != null && String(v).toLowerCase().includes(q);
        }),
      );
    }
    if (sortBy.length > 0) {
      const colMap = new Map(columns.map((c) => [c.key, c]));
      arr = [...arr].sort((a, b) => {
        for (const s of sortBy) {
          const col = colMap.get(s.key);
          if (!col) continue;
          const cmp = compareValues(col.sortValue(a), col.sortValue(b), s.dir);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
    }
    return arr;
  }, [rows, query, sortBy, columns]);

  return (
    <div className="vin-table-wrap">
      {searchPlaceholder && (
        <input
          type="text"
          className="vin-table-search"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      <div className="vin-table-scroll">
        <table className="vin-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const idx = sortBy.findIndex((s) => s.key === col.key);
                const entry = idx !== -1 ? sortBy[idx] : null;
                const arrow = entry ? (entry.dir === 'asc' ? '▲' : '▼') : '';
                return (
                  <th
                    key={col.key}
                    className={`vin-th${entry ? ' vin-th-sorted' : ''}${col.numeric ? ' vin-th-num' : ''}`}
                    onClick={(e) => handleSortClick(col.key, e)}
                    title="Click to sort. Shift+click for multi-sort."
                  >
                    <span>{col.label}</span>
                    {entry && (
                      <span className="vin-th-sort">
                        {arrow}
                        {sortBy.length > 1 && <sup>{idx + 1}</sup>}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="vin-table-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={rowKey(r, i)}>
                  {columns.map((c) => (
                    <td key={c.key} className={c.numeric ? 'vin-td-num' : undefined}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- main component ----------
export const VinDetailsPopout: React.FC<VinDetailsPopoutProps> = ({
  vehicle,
  isLoading,
  onClose,
  onBack,
  backLabel = 'Back',
  miaEnabled = false,
  onContinueInMia,
  fullScreen = false,
}) => {
  // Full screen there is nothing behind the panel to click, so a VIN opened straight from the
  // list still gets a Back button, to the list.
  const backAction = onBack ?? (fullScreen ? onClose : undefined);
  const backText = onBack ? backLabel : 'Dealers';
  const panelClass = `vin-popout-panel${fullScreen ? ' vin-popout-panel--full' : ''}`;
  // One fetch shared by the Price and Placement cards below.
  const cohortInsights = useVinCohortInsights(vehicle?.vin ?? '', !!vehicle?.vin);
  const trackedVin = vehicle?.vin;
  const trackedSeller = vehicle?.seller_id;
  useEffect(() => {
    if (!trackedVin) return undefined;
    track('client_view', 'vin_details', 'open', { vin: trackedVin, seller_uid: trackedSeller });
    return pushUsageContext('vin_details', { vin: trackedVin, seller_uid: trackedSeller });
  }, [trackedVin, trackedSeller]);

  // ---------- table column definitions ----------
  const listingHistoryCols: TableColumn<any>[] = [
    { key: 'list_time', label: 'List Time', sortValue: (r) => r.list_time, render: (r) => fmtDate(r.list_time) },
    { key: 'list_status', label: 'Status', sortValue: (r) => r.list_status, render: (r) => fmtStr(r.list_status) },
    { key: 'auction', label: 'Auction', sortValue: (r) => r.auction, render: (r) => fmtStr(r.auction) },
    { key: 'channel', label: 'Channel', sortValue: (r) => r.channel, render: (r) => fmtStr(r.channel) },
    { key: 'seller_name', label: 'Seller', sortValue: (r) => r.seller_name, render: (r) => fmtStr(r.seller_name) },
    { key: 'state', label: 'State', sortValue: (r) => r.state, render: (r) => fmtStr(r.state) },
    { key: 'adj_mmr', label: 'Adj MMR', numeric: true, sortValue: (r) => r.adj_mmr, render: (r) => fmtMoney(r.adj_mmr) },
    { key: 'buy_now_price', label: 'Buy Now', numeric: true, sortValue: (r) => r.buy_now_price, render: (r) => fmtMoney(r.buy_now_price) },
    { key: 'floor_price', label: 'Floor', numeric: true, sortValue: (r) => r.floor_price, render: (r) => fmtMoney(r.floor_price) },
    { key: 'mileage', label: 'Mileage', numeric: true, sortValue: (r) => r.mileage, render: (r) => fmtInt(r.mileage) },
    { key: 'cr_grade', label: 'CR', sortValue: (r) => r.cr_grade, render: (r) => fmtStr(r.cr_grade) },
    { key: 'sold_amt', label: 'Sold Amt', numeric: true, sortValue: (r) => r.sold_amt, render: (r) => fmtMoney(r.sold_amt) },
    { key: 'sold_time', label: 'Sold Time', sortValue: (r) => r.sold_time, render: (r) => fmtDate(r.sold_time) },
    { key: 'buyer_name', label: 'Buyer', sortValue: (r) => r.buyer_name, render: (r) => fmtStr(r.buyer_name) },
  ];

  const bidHistoryCols: TableColumn<any>[] = [
    { key: 'bid_time', label: 'Bid Time', sortValue: (r) => r.bid_time, render: (r) => fmtDate(r.bid_time) },
    { key: 'bid_offer_type', label: 'Offer Type', sortValue: (r) => r.bid_offer_type, render: (r) => fmtStr(r.bid_offer_type) },
    { key: 'buyer_name', label: 'Buyer', sortValue: (r) => r.buyer_name, render: (r) => fmtStr(r.buyer_name) },
    { key: 'bid_amt', label: 'Amount', numeric: true, sortValue: (r) => r.bid_amt, render: (r) => fmtMoney(r.bid_amt) },
    { key: 'bid_type', label: 'Type', sortValue: (r) => r.bid_type, render: (r) => fmtStr(r.bid_type) },
    { key: 'bid_channel', label: 'Channel', sortValue: (r) => r.bid_channel, render: (r) => fmtStr(r.bid_channel) },
    { key: 'bid_status', label: 'Status', sortValue: (r) => r.bid_status, render: (r) => fmtStr(r.bid_status) },
    { key: 'closed_time', label: 'Closed Time', sortValue: (r) => r.closed_time, render: (r) => fmtDate(r.closed_time) },
  ];

  // ---------- early returns ----------
  // Keep the panel chrome mounted: returning null here strands the user, because the
  // back / close controls disappear while the parent still considers the pop-out open.
  if (!vehicle && !isLoading) {
    return (
      <div className={panelClass} role="dialog" aria-label="VIN details" aria-modal={fullScreen || undefined}>
        <div className="vin-popout-header">
          <div className="vin-popout-title">
            {backAction && (
              <button className="vin-popout-back" onClick={backAction} title={backText}>
                ← {backText}
              </button>
            )}
            <h2>Vehicle unavailable</h2>
          </div>
          <button className="vin-popout-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="vin-popout-empty">
          <p className="vin-popout-empty-main">Vehicle data is not available for this VIN.</p>
          <p className="vin-popout-empty-sub">
            It may have sold or been removed since this list was last refreshed. Close this
            panel to continue working.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={panelClass} role="dialog" aria-label="VIN details" aria-modal={fullScreen || undefined}>
      <div className="vin-popout-header">
        <div className="vin-popout-title">
          {backAction && (
            <button className="vin-popout-back" onClick={backAction} title={backText}>
              ← {backText}
            </button>
          )}
          <h2>{vehicle ? `${vehicle.model_year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim() || 'Vehicle' : 'Loading…'}</h2>
          {vehicle && <span className="vin-popout-vin">{vehicle.vin}</span>}
        </div>
        <button className="vin-popout-close" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {isLoading && <div className="vin-popout-loading">Loading vehicle details…</div>}

      {!isLoading && vehicle && (
        <div className="vin-popout-body">
          {/* Sticky headline strip — the numbers that drive the call, kept in
              view while the cards below scroll. Buy Now / Base MMR live on the
              price ladder instead, where they carry position rather than just a
              value. */}
          <div className="vin-summary-strip">
            <SummaryChip label="Floor Price" value={fmtMoney(vehicle.floor_price)} tooltip="Seller-set minimum reserve price" />
            <SummaryChip label="Adj MMR" value={fmtMoney(vehicle.adjusted_mmr)} tooltip="Adjusted MMR — Manheim Market Report value adjusted for this vehicle's CR / mileage" />
            <SummaryChip label="CR Grade" value={fmtStr(vehicle.cr_grade)} tooltip="Condition Report grade (0–5 scale)" />
            <SummaryChip
              label="Light"
              value={<LightBadge light={vehicle.light_status} source={vehicle.light_source} titleAbsent={vehicle.title_absent_flg} />}
            />
            <SummaryChip label="List Days" value={fmtInt(vehicle.list_days)} tooltip="Days the vehicle has been listed in its current run" />
            <SummaryChip label="Mileage" value={fmtInt(vehicle.mileage)} tooltip="Reported odometer reading" />
            <SummaryChip label="Active Bids" value={fmtInt(vehicle.active_bids)} tooltip="Number of active proxy/live bids on the current listing" />
          </div>

          <div className="vin-grid">

          {/* RDS-3.5: Mia's "Where to focus" replaces the old AI Recommendations card. */}
          {miaEnabled && (
            <div className="vin-card-wide">
              <RdsMiaSummary key={vehicle.vin} kind="vin" id={vehicle.vin} onContinue={onContinueInMia} />
            </div>
          )}

          {/* 1. Is it priced right? Every price point the pop-out carries is on
                 the ladder, lane-separated so the retail figures can't be
                 mistaken for each other. */}
          <Card title="Price & Position" className="vin-card-wide">
            <VinCohortInsightsCard
              vin={vehicle.vin}
              sections={['price']}
              insights={cohortInsights}
              pricing={{
                floorPrice: vehicle.floor_price,
                buyNowPrice: vehicle.buy_now_price,
                baseMmr: vehicle.base_mmr,
                adjMmr: vehicle.adjusted_mmr,
                mmrRange: vehicle.mmr_range,
                retailValue: vehicle.retail_value,
                retailValueRange: vehicle.retail_value_range,
                retailListPrice: vehicle.retail_list_price,
                retailRmi: vehicle.retail_rmi,
                retailAvgListPrice: vehicle.retail_avg_list_price,
                retailSpread: vehicle.retail_spread,
                retailRetention: vehicle.retail_retention,
              }}
            />
          </Card>

          {/* 2. Is anyone interested, and who? Listing-level engagement sits
                 next to the per-buyer detail it summarises. */}
          <Card title="Demand & Buyers" className="vin-card-wide">
            <div className="vin-kv-grid">
              <Kv
                label="VDP Views (avg/day)"
                value={fmtInt(vehicle.average_daily_vdp_views)}
                hint="Average VDP views per day since this listing started, capped at the 45-day activity window."
              />
              <Kv
                label="Client VDP Views"
                value={fmtInt(vehicle.total_client_vdp_views)}
                hint="Distinct buyer accounts that viewed this VIN in the last 45 days, across every listing of it."
              />
              <Kv
                label="Workbook Saves"
                value={fmtInt(vehicle.client_workbook_saves)}
                hint="Distinct buyer accounts currently holding this VIN in a workbook (adds net of removes), last 45 days."
              />
              <Kv label="Photos" value={fmtInt(vehicle.photo_count)} />
              <Kv label="Active Bids" value={fmtInt(vehicle.active_bids)} />
              <Kv label="Total Bids" value={fmtInt(vehicle.total_bids)} />
              <Kv label="Active High Bid" value={fmtMoney(vehicle.active_high_bid)} />
              <Kv label="Buyer Coverage" value={fmtInt(vehicle.buyer_coverage)} hint={VMA_FIELD_HELP.buyer_coverage} />
            </div>
            <VinBuyersCard vin={vehicle.vin} />
          </Card>

          {/* 3. Where and how should it run? Market conditions and the cohort's
                 venue / channel ranking answer the same question. */}
          <Card title="Market & Placement" className="vin-card-wide">
            {(vehicle.wholesale_supply_level || vehicle.wholesale_demand_level ||
              vehicle.retail_supply_level || vehicle.retail_demand_level) && (
              <div className="vin-levels">
                <LevelBadge label="WS Supply" level={vehicle.wholesale_supply_level} tones={WS_SUPPLY_TONE} hint={VMA_FIELD_HELP.wholesale_supply_level} />
                <LevelBadge label="WS Demand" level={vehicle.wholesale_demand_level} tones={WS_DEMAND_TONE} hint={VMA_FIELD_HELP.wholesale_demand_level} />
                <LevelBadge label="Retail Supply" level={vehicle.retail_supply_level} tones={RETAIL_SUPPLY_TONE} hint={VMA_FIELD_HELP.retail_supply_level} />
                <LevelBadge label="Retail Demand" level={vehicle.retail_demand_level} tones={RETAIL_DEMAND_TONE} hint={VMA_FIELD_HELP.retail_demand_level} />
              </div>
            )}

            <DaysBars
              bars={[
                { key: 'listed', label: 'This listing, so far', value: vehicle.list_days, tone: 'self' },
                {
                  key: 'ws_dts', label: 'Wholesale days to sell', value: vehicle.wholesale_avg_days_to_sell,
                  tone: 'wholesale', hint: VMA_FIELD_HELP.wholesale_avg_days_to_sell,
                },
                {
                  key: 'ws_supply', label: 'Wholesale days supply', value: vehicle.wholesale_days_supply,
                  tone: 'wholesale', hint: VMA_FIELD_HELP.wholesale_days_supply,
                },
                {
                  key: 'rt_dts', label: 'Retail days to sell', value: vehicle.retail_avg_days_to_sell,
                  tone: 'retail', hint: VMA_FIELD_HELP.retail_avg_days_to_sell,
                },
                {
                  key: 'rt_supply', label: 'Retail days supply', value: vehicle.retail_days_supply,
                  tone: 'retail', hint: VMA_FIELD_HELP.retail_days_supply,
                },
              ]}
            />
            <VelocityCaption daysListed={vehicle.list_days} avgDaysToSell={vehicle.wholesale_avg_days_to_sell} />

            {(vehicle.wholesale_sales_efficiency != null ||
              vehicle.wholesale_sales_effectiveness != null) && (
              <div className="vin-stat-row">
                <div className="vin-stat" title={VMA_FIELD_HELP.wholesale_sales_efficiency}>
                  <span className="vin-stat-label">WS Sales Efficiency</span>
                  <span className="vin-stat-value">
                    {vehicle.wholesale_sales_efficiency != null
                      ? `${Math.round(vehicle.wholesale_sales_efficiency)}%`
                      : '–'}
                  </span>
                  <span className="vin-stat-note">of listed units sold</span>
                </div>
                <div className="vin-stat" title={VMA_FIELD_HELP.wholesale_sales_effectiveness}>
                  <span className="vin-stat-label">WS Sales Effectiveness</span>
                  <span className="vin-stat-value">
                    {vehicle.wholesale_sales_effectiveness != null
                      ? `${Math.round(vehicle.wholesale_sales_effectiveness)}%`
                      : '–'}
                  </span>
                  <span className="vin-stat-note">of expected value realized</span>
                </div>
              </div>
            )}

            <p className="vin-panel-note">{VMA_PANEL_NOTE}</p>

            <VinCohortInsightsCard
              vin={vehicle.vin}
              sections={['channels', 'venues']}
              insights={cohortInsights}
              currentAuction={vehicle.auction}
            />
          </Card>

          {/* 4. Reference: what the unit is, how it's listed, how it inspected. */}
          <Card title="Vehicle, Listing & Condition" className="vin-card-wide">
            <div className="vin-kv-grid">
              <Kv label="Year" value={fmtStr(vehicle.model_year)} />
              <Kv label="Make" value={fmtStr(vehicle.make)} />
              <Kv label="Model" value={fmtStr(vehicle.model)} />
              <Kv label="Trim" value={fmtStr(vehicle.trim)} />
              <Kv label="Mileage" value={fmtInt(vehicle.mileage)} />
              <Kv label="Color" value={fmtStr(vehicle.color)} />
              <Kv label="Seller" value={fmtStr(vehicle.seller_name)} />
              <Kv label="List Status" value={fmtStr(vehicle.list_status)} />
              <Kv label="Days Listed" value={fmtInt(vehicle.list_days)} />
              <Kv label="Channel" value={fmtStr(vehicle.channel)} />
              <Kv label="Auction" value={fmtStr(vehicle.auction)} />
              <Kv
                label="VDP"
                value={
                  vehicle.vdp_url ? (
                    <a href={vehicle.vdp_url} target="_blank" rel="noopener noreferrer">Open VDP ↗</a>
                  ) : (
                    '–'
                  )
                }
              />
            </div>

            <Expander label="Full specs">
              <div className="vin-kv-grid">
                <Kv label="Body Style" value={fmtStr(vehicle.body_style)} />
                <Kv label="Doors" value={fmtStr(vehicle.doors)} />
                <Kv label="Drivetrain" value={fmtStr(vehicle.drivetrain)} />
                <Kv label="Engine" value={fmtStr(vehicle.engine)} />
                <Kv label="Fuel Type" value={fmtStr(vehicle.fuel_type)} />
                <Kv label="Transmission" value={fmtStr(vehicle.transmission)} />
                <Kv label="Vehicle Type" value={fmtStr(vehicle.vehicle_type)} />
                <Kv label="VIN" value={vehicle.vin} />
              </div>
            </Expander>

            <Expander label="Listing detail">
              <div className="vin-kv-grid">
                <Kv label="Seller ID" value={fmtStr(vehicle.seller_id)} />
                <Kv label="List Time" value={fmtDate(vehicle.list_time)} />
                <Kv label="Facilitation" value={fmtStr(vehicle.facilitation)} />
                <Kv label="Site Location" value={fmtStr(vehicle.site_location)} />
                <Kv label="State" value={fmtStr(vehicle.state)} />
                <Kv label="Acquired" value={fmtDate(vehicle.seller_acquisition_time)} />
                <Kv label="Ownership Days" value={fmtInt(vehicle.seller_ownership_days)} />
                <Kv label="Acquisition Method" value={fmtStr(vehicle.seller_acquisition_method)} />
                <Kv label="Listing Occurrences" value={fmtInt(vehicle.listing_occurences)} />
              </div>
            </Expander>

            <Expander label="Inspection">
              <div className="vin-kv-grid">
                <Kv label="CR Grade" value={fmtStr(vehicle.cr_grade)} />
                <Kv label="Inspect Status" value={fmtStr(vehicle.inspect_status)} />
                <Kv label="Inspect Time" value={fmtDate(vehicle.inspect_time)} />
                <Kv label="Inspection Channel" value={fmtStr(vehicle.inspection_channel)} />
                <Kv label="Inspection Type" value={fmtStr(vehicle.inspection_type)} />
                <Kv
                  label="Light"
                  value={<LightBadge light={vehicle.light_status} source={vehicle.light_source} titleAbsent={vehicle.title_absent_flg} />}
                />
                <Kv
                  label="Condition Report"
                  value={
                    vehicle.cr_link ? (
                      <a href={vehicle.cr_link} target="_blank" rel="noopener noreferrer">Open CR ↗</a>
                    ) : (
                      '–'
                    )
                  }
                />
              </div>
              {vehicle.cr_link && (
                <iframe
                  src={vehicle.cr_link}
                  title="Condition Report"
                  className="vin-cr-iframe"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              )}
            </Expander>
          </Card>

          {/* 5. History tables (full width) */}
          <Card title="History" className="vin-card-wide">
            {vehicle.listing_history && vehicle.listing_history.length > 0 && (
              <Expander label={`Listing history (${vehicle.listing_history.length})`}>
                <SortableTable
                  columns={listingHistoryCols}
                  rows={vehicle.listing_history as any[]}
                  searchPlaceholder="Search listing history"
                  rowKey={(r, i) => `${r.list_time}-${i}`}
                />
              </Expander>
            )}
            {vehicle.bid_history && vehicle.bid_history.length > 0 ? (
              <Expander label={`Bid history (${vehicle.bid_history.length})`}>
                <SortableTable
                  columns={bidHistoryCols}
                  rows={vehicle.bid_history as any[]}
                  searchPlaceholder="Search bids"
                  rowKey={(r, i) => `${r.bid_time}-${i}`}
                />
              </Expander>
            ) : (
              <div className="vin-empty">No bid history</div>
            )}
          </Card>

          </div>
        </div>
      )}
    </div>
  );
};
